import * as Sentry from '@sentry/vue';
import { AssistantService } from '@/api/services/assistant.service';
import { MenuImportsService } from '@/api/services/menu-imports.service';
import {
  OPTION_GROUPS_CHANGED_EVENT,
  STOCK_CHANGED_EVENT,
} from '@/domain/catalog/catalog-events';
import type { IStockChangedEvent } from '@/domain/catalog/catalog-events';
import { MENU_UPDATE_UNDONE_EVENT } from '@/domain/menu-import/update-undo';
import {
  getServerErrorCode,
  getServerErrorStatus,
} from '@/domain/reservations/reservation-errors';
import { menuImportErrorKey } from '@/domain/store-setup/setup-errors';
import type {
  IAssistantUndoResult,
  TAssistantErrorBody,
} from '@/interfaces/assistant.interface';
import type { IUndoUpdateResponse } from '@/interfaces/IMenuImport';
import { EventBus } from '@/modules/EventBus';
import type {
  IGhCard,
  IGhCardActionEvent,
  IGhCardActionResult,
  IGhPayload,
} from '../gpthelp-host-contract';
import {
  ACTIVITY_VIEW_LIMIT,
  activityActionTitle,
  activityEntryCard,
  changesListCard,
  mergeActivity,
} from '../cards/activity-cards';
import type { TActivityEntry } from '../cards/activity-cards';
import { adminCardFormat } from '../cards/admin-card-format';
import { clampText, fitText } from '../cards/card-text';
import type { ICardFormat } from '../cards/card-text';
import { exportGoneCard } from '../cards/export-cards';
import { planName } from '../cards/lock-cards';
import {
  undoKeptText,
  undoneCard,
  undoWindowCard,
  writesOffText,
} from '../cards/result-cards';
import type { IUndoneCardInput } from '../cards/result-cards';
import { downloadExport } from '../exports/gpthelp-export-registry';
import { buildGpthelpToolGate } from '../tools/tool-gate';
import {
  buildScopeKey,
  ensureRestaurantScopeOrThrow,
} from '../tools/tool-scope';
import { switchedOff } from '../tools/tool-switch';
import type { IGpthelpToolScope } from '../tools/tool-types';
import type {
  IActionRun,
  IAssistantCardActionHost,
} from './assistant-action-run';
import {
  NOTICE_MAX,
  payloadString,
  refusalResult,
  requirePayloadString,
} from './assistant-action-run';
import { activityContextFor } from './assistant-activity';
import { readAssistantError } from './assistant-errors';
import {
  applyProposalAction,
  discardProposalAction,
  refreshProposalAction,
} from './assistant-proposal-actions';
import { forbiddenTitleKey, writesOffOf } from './assistant-refusals';
import {
  buildSupportMessage,
  isSupportTopic,
  supportSenderOf,
} from './assistant-support';

/** Finds the change an undo touched in the fresh activity. */
type TEntryMatch = (entry: TActivityEntry) => boolean;

/** The list card of `assistant_listChanges` shows at most this many changes. */
const CHANGES_MAX = 5;

/** The actions that undo a change: store-wide (plan §8), never an apply. */
const UNDO_ACTIONS = ['undo', 'undoPhoto'];

/** The actions of a proposal's cards: its payload names it and its branch. */
const PROPOSAL_ACTIONS = ['apply', 'discard', 'refresh'];

/** The host tool that shows the support cards. */
const SUPPORT_TOOL = 'support_handoff';

/** A payload made for another store or branch than the current one. */
function isOtherScope(payload: IGhPayload, scope: IGpthelpToolScope): boolean {
  const { restaurantId, branchId } = payload;
  return (
    (typeof restaurantId === 'number' && restaurantId !== scope.restaurantId) ||
    (typeof branchId === 'number' && branchId !== scope.branchId)
  );
}

/**
 * The card an undo was tapped on lists changes («Cambios del asistente» or
 * the list card): it is rebuilt, never replaced by a one-line card. Null
 * for any other card.
 */
function rebuildKindOf(payload: IGhPayload): 'activity' | 'changes' | null {
  return payload.rebuild === 'activity' || payload.rebuild === 'changes'
    ? payload.rebuild
    : null;
}

/**
 * The line an Action API refusal leaves under a card that lists changes;
 * null for the refusals the widget reports itself.
 */
function refusalNotice(
  body: TAssistantErrorBody | null,
  fmt: ICardFormat
): string | null {
  switch (body?.code) {
    case 'ASSISTANT_UNDO_WINDOW':
      return clampText(fmt.t('assistant.result.undoWindow'), NOTICE_MAX);
    case 'ASSISTANT_QUOTA':
      return clampText(fmt.t('assistant.result.quota'), NOTICE_MAX);
    case 'ASSISTANT_UPDATE_REQUIRED':
      return clampText(fmt.t('assistant.result.updateRequired'), NOTICE_MAX);
    case 'ASSISTANT_WRITES_OFF':
      return clampText(
        writesOffText(writesOffOf(body.details), fmt),
        NOTICE_MAX
      );
    case 'ASSISTANT_FORBIDDEN':
      return clampText(fmt.t(forbiddenTitleKey(body.details)), NOTICE_MAX);
    case 'ASSISTANT_PLAN_REQUIRED':
      return clampText(
        fmt.t('assistant.lock.plan', {
          plan: planName(body.details?.minTier ?? 1),
        }),
        NOTICE_MAX
      );
    default:
      return null;
  }
}

/** The card an undo was tapped on, rebuilt, and the change as it now reads. */
interface IRebuiltCard {
  card: IGhCard | null;
  entry: TActivityEntry | null;
}

/**
 * The card an undo was tapped on, rebuilt from the fresh activity: the
 * callout of «Cambios del asistente» or the whole list card, so the change
 * reads «Deshecho» (or no longer offers «Deshacer») in place. No card for
 * any other card, or when the activity cannot be read.
 */
async function rebuildChangesCard(
  run: IActionRun,
  matches: TEntryMatch
): Promise<IRebuiltCard> {
  const { event, payload, host, scope, fmt } = run;
  const kind = rebuildKindOf(payload);
  if (!kind) return { card: null, entry: null };
  const limit =
    kind === 'changes'
      ? Math.min(CHANGES_MAX, Math.max(1, Number(payload.limit) || 1))
      : ACTIVITY_VIEW_LIMIT;
  try {
    const response = await AssistantService.listActions(scope.restaurantId, {
      limit,
    });
    const context = activityContextFor(host.deps, scope);
    const entry = mergeActivity(response).find(matches) ?? null;
    if (kind === 'changes')
      return {
        card: changesListCard(event.cardId, response, limit, context, fmt),
        entry,
      };
    return {
      card: entry
        ? { ...activityEntryCard(entry, context, fmt), id: event.cardId }
        : null,
      entry,
    };
  } catch (error) {
    console.warn('[assistant] activity unavailable after an undo', error);
    return { card: null, entry: null };
  }
}

/**
 * A refusal under a card that lists changes: the card stays with `notice`
 * under it; after «too late» (`rebuild`) it is rebuilt first, so the change
 * no longer offers «Deshacer».
 */
async function refusalUnderCard(
  run: IActionRun,
  matches: TEntryMatch,
  notice: string,
  rebuild: boolean
): Promise<IGhCardActionResult> {
  if (!rebuild) return { notice };
  const { card } = await rebuildChangesCard(run, matches);
  return card ? { card, notice } : { notice };
}

/**
 * After an undo: the rebuilt card, with the rows kept because someone
 * changed them afterwards under it (plan §9.9 `result.kept`; `keptKeys`
 * tells products from other changes, plan P2-4); without one, the Undone
 * card, which says it itself (and what it removed: `added`).
 */
function undoneResult(
  run: IActionRun,
  card: IGhCard | null,
  kept: number,
  keptKeys: readonly string[],
  added: Pick<IUndoneCardInput, 'created' | 'createdAny'>,
  conversationAppliedCount?: number
): IGhCardActionResult {
  const { event, fmt } = run;
  if (!card)
    return {
      card: undoneCard(
        {
          id: event.cardId,
          kept,
          keptKeys,
          ...added,
          conversationAppliedCount,
        },
        fmt
      ),
    };
  return kept > 0
    ? {
        card,
        notice: clampText(undoKeptText(kept, keptKeys, fmt), NOTICE_MAX),
      }
    : { card };
}

/**
 * What the undo of an inventory change put back: the branch its receipt
 * names («Cambios del asistente» does not know it); every product and
 * stock item of it may have changed.
 */
function undoneStockChange(run: IActionRun): IStockChangedEvent {
  const { stockBranchId } = run.payload;
  return {
    restaurantId: run.scope.restaurantId,
    branchId: typeof stockBranchId === 'number' ? stockBranchId : null,
    itemIds: null,
    stockItemIds: null,
  };
}

async function undoAction(run: IActionRun): Promise<IGhCardActionResult> {
  const { payload, host, scope, fmt } = run;
  const actionId = requirePayloadString(run, 'actionId');
  const matches: TEntryMatch = (entry) =>
    entry.kind === 'action' && entry.item.id === actionId;

  let result: IAssistantUndoResult;
  try {
    result = await AssistantService.undo(scope.restaurantId, actionId);
  } catch (error) {
    if (!rebuildKindOf(payload)) {
      const refusal = refusalResult(error, run);
      if (refusal) return refusal;
      throw error;
    }
    const body = readAssistantError(error);
    const notice = refusalNotice(body, fmt);
    if (!notice) throw error;
    return refusalUnderCard(
      run,
      matches,
      notice,
      body?.code === 'ASSISTANT_UNDO_WINDOW'
    );
  }
  Sentry.addBreadcrumb({
    category: 'assistant',
    message: 'undo',
    data: { actionId, reverted: result.reverted, kept: result.kept },
  });
  // It changed option groups themselves: the pages that list them reload (a
  // group no live product uses changes without a realtime catalogUpdate).
  if (payload.optionGroups === true)
    EventBus.$emit(OPTION_GROUPS_CHANGED_EVENT, scope.restaurantId);
  // It put back inventory: the pages that show it follow (plan P5-3). The
  // undo names no stock item nor product, so any of its branch's.
  if (payload.stock === true && result.reverted > 0)
    EventBus.$emit(STOCK_CHANGED_EVENT, undoneStockChange(run));

  const rebuilt = await rebuildChangesCard(run, matches);
  // The model reads what just happened in the next turn's context.
  host.deps.assistantStore.addRecent({
    actionId,
    status: 'undone',
    title:
      rebuilt.entry?.kind === 'action'
        ? activityActionTitle(rebuilt.entry.item, fmt)
        : payloadString(payload.title) ?? fmt.t('assistant.result.undone'),
    at: result.undoneAt,
    scopeKey: buildScopeKey(scope),
  });
  // A receipt sits in the conversation that made the change: its badge follows.
  return undoneResult(
    run,
    rebuilt.card,
    result.kept,
    result.keptRows.map((row) => row.key),
    {
      created: payload.created === true,
      createdAny: payload.createdAny === true,
    },
    rebuildKindOf(payload) ? undefined : result.conversationAppliedCount
  );
}

async function undoPhotoAction(run: IActionRun): Promise<IGhCardActionResult> {
  const { event, payload, fmt } = run;
  const sessionId = requirePayloadString(run, 'sessionId');
  const matches: TEntryMatch = (entry) =>
    entry.kind === 'photo' && entry.update.sessionId === sessionId;

  let result: IUndoUpdateResponse;
  try {
    result = await MenuImportsService.undoUpdate(sessionId);
  } catch (error) {
    const code = getServerErrorCode(error);
    if (code === 'NOT_UNDOABLE')
      return rebuildKindOf(payload)
        ? refusalUnderCard(
            run,
            matches,
            clampText(fmt.t('assistant.result.undoWindow'), NOTICE_MAX),
            true
          )
        : { card: undoWindowCard(event.cardId, fmt) };
    const key = menuImportErrorKey(code, getServerErrorStatus(error));
    if (key) return { notice: clampText(fmt.t(key), NOTICE_MAX) };
    throw error;
  }
  // The pages that show the menu reload, as after the dialog's undo.
  EventBus.$emit(MENU_UPDATE_UNDONE_EVENT);

  const rebuilt = await rebuildChangesCard(run, matches);
  // An update from photos changes products only.
  return undoneResult(run, rebuilt.card, result.kept, [], { created: false });
}

/**
 * «Descargar»: saves the report, as often as it is tapped while the file
 * lasts (30 min, in this tab); afterwards the card says it is gone.
 */
function downloadAction(run: IActionRun): IGhCardActionResult | void {
  const exportId = payloadString(run.payload.exportId);
  if (exportId && downloadExport(exportId)) return;
  return { card: exportGoneCard(run.event.cardId, run.fmt) };
}

/**
 * «Escribir a soporte» (plan §8 R4): builds the card's message again from its
 * topic and request, in the card's language and from the store and branch it
 * names (`runAssistantCardAction` checks them), and opens it with the support
 * link of the current config, never a link from the card. The card stays.
 * While `support_handoff` is switched off (its kill switch, or the config is
 * unknown), cards already shown open nothing: a notice points to «Hablar con
 * una persona» (⋯), which opens the same support link.
 */
async function supportAction(run: IActionRun): Promise<IGhCardActionResult> {
  const { payload, host, scope, gate, fmt } = run;
  const supportUrl = gate.supportUrl;
  if (!supportUrl) throw new Error('This store has no support link.');
  if (switchedOff(SUPPORT_TOOL, gate))
    return {
      notice: clampText(
        fmt.t('assistant.support.off', {
          human: fmt.t('assistant.menu.human'),
        }),
        NOTICE_MAX
      ),
    };
  const topic = payload.topic;
  if (!isSupportTopic(topic))
    throw new Error('The support action has no topic.');
  if (
    typeof payload.restaurantId !== 'number' ||
    typeof payload.branchId !== 'number'
  )
    throw new Error('The support action has no store or branch.');
  const locale = requirePayloadString(run, 'locale');
  const { url } = buildSupportMessage({
    fmt:
      locale === fmt.locale
        ? fmt
        : adminCardFormat(host.deps, scope.timeZone, locale),
    sender: supportSenderOf(host.deps),
    topic,
    summary: requirePayloadString(run, 'summary'),
    supportUrl,
  });
  await host.openExternal(url);
  return { notice: clampText(fmt.t('assistant.support.opened'), NOTICE_MAX) };
}

/** Actions that call the Action API or reload the admin: they need a connection. */
const ONLINE_ACTIONS = [...PROPOSAL_ACTIONS, ...UNDO_ACTIONS, 'reload'];

/**
 * Runs a host action of an assistant card (plan §9.9): checks the store
 * and branch it was made for and the connection, then acts and returns the
 * card that replaces it or a notice. Plain functions returning cards: no
 * component state changes after an await (renderer safety).
 */
export async function runAssistantCardAction(
  event: IGhCardActionEvent,
  host: IAssistantCardActionHost
): Promise<IGhCardActionResult | void> {
  const scope = ensureRestaurantScopeOrThrow(host.deps);
  const run: IActionRun = {
    event,
    payload: event.payload ?? {},
    host,
    scope,
    gate: buildGpthelpToolGate(host.deps),
    fmt: adminCardFormat(host.deps, scope.timeZone),
  };

  if (isOtherScope(run.payload, scope)) {
    // A support message names its store and branch: it is sent from them.
    if (event.actionId === 'support')
      return {
        notice: clampText(
          run.fmt.t('assistant.support.otherScope'),
          NOTICE_MAX
        ),
      };
    const scopeName = payloadString(run.payload.scopeName) ?? '';
    // An undo keeps its card: the change belongs to another store.
    if (UNDO_ACTIONS.includes(event.actionId))
      return {
        notice: fitText(
          run.fmt,
          'assistant.result.otherStore',
          { store: scopeName },
          'store',
          NOTICE_MAX
        ),
      };
    // A proposal keeps its card and still waits: it applies from its branch.
    return {
      notice: fitText(
        run.fmt,
        'assistant.result.otherScope',
        { branch: payloadString(run.payload.branchName) ?? scopeName },
        'branch',
        NOTICE_MAX
      ),
    };
  }
  if (ONLINE_ACTIONS.includes(event.actionId) && !host.isOnline())
    return { notice: run.fmt.t('assistant.result.offline') };

  switch (event.actionId) {
    case 'apply':
      return applyProposalAction(run);
    case 'discard':
      return discardProposalAction(run);
    case 'refresh':
      return refreshProposalAction(run);
    case 'download':
      return downloadAction(run);
    case 'reload':
      host.reload();
      return;
    case 'undo':
      return undoAction(run);
    case 'undoPhoto':
      return undoPhotoAction(run);
    case 'support':
      return supportAction(run);
    default:
      throw new Error(`Unknown assistant card action: ${event.actionId}`);
  }
}
